import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, TrendingUp, TrendingDown, Minus, Heart, Square, Share2, BarChart2, Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { getBeltConnectionState, useDeviceWebSocket, type BeltConnectionState } from '../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../hooks/useHeartbeatSound';
import { getSessions, endSession } from '../api/sessions';
const WAVEFORM_SAMPLES_PER_SECOND = 20;
const MAX_QUEUED_WAVEFORM_SAMPLES = 500;
const MAX_DISPLAYED_WAVEFORM_SAMPLES = 200;
type BeltStatusPresentation = {
  label: string;
  message: string;
  className: string;
  dotClassName: string;
  showPing: boolean;
};
function getBeltStatusPresentation(state: BeltConnectionState): BeltStatusPresentation {
  switch (state) {
    case 'live':
    case 'signal_weak':
      return {
        label: 'Live · Belt connected',
        message: 'Live monitoring is active.',
        className: 'bg-success/15 text-success',
        dotClassName: 'bg-success',
        showPing: true
      };
    case 'reconnecting':
      return {
        label: 'Reconnecting',
        message: 'Reconnecting to belt…',
        className: 'bg-amber-400/15 text-amber-300',
        dotClassName: 'bg-amber-300',
        showPing: false
      };
    case 'offline':
      return {
        label: 'Belt offline',
        message: "Can't reach your belt — check it's powered on and nearby.",
        className: 'bg-danger/15 text-danger',
        dotClassName: 'bg-danger',
        showPing: false
      };
    default:
      return {
        label: 'Waiting for belt',
        message: 'Turn on your belt to begin monitoring.',
        className: 'bg-white/10 text-white/55',
        dotClassName: 'bg-white/40',
        showPing: false
      };
  }
}
function appendToPlaybackQueue(queue: number[], batch: number[]) {
  if (batch.length >= MAX_QUEUED_WAVEFORM_SAMPLES) {
    queue.length = 0;
    queue.push(...batch.slice(-MAX_QUEUED_WAVEFORM_SAMPLES));
    return;
  }
  const overflow = queue.length + batch.length - MAX_QUEUED_WAVEFORM_SAMPLES;
  if (overflow > 0) queue.splice(0, overflow);
  queue.push(...batch);
}
export function MonitorScreen() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const {
    isConnected,
    lastMessageAgeSeconds,
    waveformData,
    babyWaveBatch,
    motherWaveBatch,
    bpm: legacyBpm,
    fetalBpm,
    maternalBpm,
    piezoValue
  } = useDeviceWebSocket();
  const rawBpm = fetalBpm ?? legacyBpm;
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(rawBpm, isConnected);
  // Session State
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showEndSession, setShowEndSession] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  // Stats State
  const [bpm, setBpm] = useState<number | null>(null);
  const [minBpm, setMinBpm] = useState<number | null>(null);
  const [maxBpm, setMaxBpm] = useState<number | null>(null);
  const [avgBpm, setAvgBpm] = useState<number | null>(null);
  const bpmHistoryRef = useRef<number[]>([]);
  const lastRenderedBpmRef = useRef<number | null>(null);
  const lastBpmUpdateTimeRef = useRef<number>(0);
  // Trend State
  const [trend, setTrend] = useState<'up' | 'down' | 'flat'>('flat');
  // Signal Quality State
  const [signalQuality, setSignalQuality] = useState<'good' | 'fair' | 'poor' | 'none'>('none');
  const recentPiezoRef = useRef<number[]>([]);
  // Baby Activity State
  const [kickCount, setKickCount] = useState(0);
  const [showKick, setShowKick] = useState(false);
  const [showKickText, setShowKickText] = useState(false);
  const lastKickTimeRef = useRef(0);
  // Canvas Refs
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const miniCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  // Queue batches from the WebSocket and render them at a stable 50 Hz.
  const babyQueueRef = useRef<number[]>([]);
  const motherQueueRef = useRef<number[]>([]);
  const mainBufferRef = useRef<number[]>([]);
  const miniBufferRef = useRef<number[]>([]);
  const scrollOffsetRef = useRef<number>(0);
  const hasHydratedWaveformRef = useRef(false);
  const initialWaveformBatchRef = useRef<number[] | null>(null);
  const hasLiveBabyPlaybackRef = useRef(false);
  const babySignalWeakRef = useRef(false);
  const [isBabySignalWeak, setIsBabySignalWeak] = useState(false);
  const motherSignalWeakRef = useRef(true);
  const [isMotherSignalWeak, setIsMotherSignalWeak] = useState(true);
  // Landscape State
  const [isLandscape, setIsLandscape] = useState(false);
  const [showLandscapeStats, setShowLandscapeStats] = useState(true);
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const beltConnectionState = getBeltConnectionState(lastMessageAgeSeconds, isBabySignalWeak);
  const beltStatus = getBeltStatusPresentation(beltConnectionState);
  // --- Session Management ---
  useEffect(() => {
    if (!token) return;
    const checkSession = async () => {
      try {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const sessions = await getSessions(token, {
          from: startOfToday.toISOString()
        });
        const active = sessions.find((s: any) => s.sessionStatus === 'active');
        if (active) {
          setActiveSessionId(active.id);
        }
      } catch (err) {
        // console.error('Failed to check active session', err)
      }
    };
    checkSession();
  }, [token]);
  const handleEndSession = async () => {
    if (!token || !activeSessionId) return;
    setIsEndingSession(true);
    try {
      await endSession(token, activeSessionId);
      setShowEndSession(false);
      navigate('/app/history');
    } catch (err) {
      // console.error('Failed to end session', err)
      setIsEndingSession(false);
    }
  };
  // --- Orientation Handling ---
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Restore the whole shared fetal trace immediately. The most recent batch is
  // already represented there, so do not replay it into the local queue.
  useEffect(() => {
    if (hasHydratedWaveformRef.current || waveformData.length === 0) return;
    mainBufferRef.current = waveformData.slice(-MAX_DISPLAYED_WAVEFORM_SAMPLES);
    babyQueueRef.current = [];
    initialWaveformBatchRef.current = babyWaveBatch;
    hasHydratedWaveformRef.current = true;
  }, [babyWaveBatch, waveformData]);
  useEffect(() => {
    if (babyWaveBatch.length === 0) return;
    if (initialWaveformBatchRef.current === babyWaveBatch) {
      initialWaveformBatchRef.current = null;
      return;
    }
    appendToPlaybackQueue(babyQueueRef.current, babyWaveBatch);
    hasLiveBabyPlaybackRef.current = true;
    babySignalWeakRef.current = false;
    setIsBabySignalWeak(false);
  }, [babyWaveBatch]);
  useEffect(() => {
    if (motherWaveBatch.length === 0) return;
    appendToPlaybackQueue(motherQueueRef.current, motherWaveBatch);
    motherSignalWeakRef.current = false;
    setIsMotherSignalWeak(false);
  }, [motherWaveBatch]);
  // --- Data Processing ---
  useEffect(() => {
    if (!isConnected) {
      setSignalQuality('none');
      return;
    }
    // Calculate Signal Quality
    recentPiezoRef.current.push(piezoValue);
    if (recentPiezoRef.current.length > 20) recentPiezoRef.current.shift();
    if (recentPiezoRef.current.length === 20) {
      const min = Math.min(...recentPiezoRef.current);
      const max = Math.max(...recentPiezoRef.current);
      const range = max - min;
      if (range > 200 && range < 3000) setSignalQuality('good');else if (range >= 50 && range <= 200) setSignalQuality('fair');else setSignalQuality('poor');
    }
    // Kick Detection
    if (isConnected && piezoValue > 2000) {
      const now = Date.now();
      if (now - lastKickTimeRef.current > 1000) {
        lastKickTimeRef.current = now;
        setKickCount(prev => prev + 1);
        setShowKick(true);
        setShowKickText(true);
        setTimeout(() => setShowKick(false), 300);
        setTimeout(() => setShowKickText(false), 1500);
      }
    }
  }, [piezoValue, isConnected]);
  // Throttled BPM Updates & Stats
  useEffect(() => {
    if (!rawBpm || !isConnected) return;
    const now = Date.now();
    const timeSinceLastUpdate = now - lastBpmUpdateTimeRef.current;
    const valueDiff = lastRenderedBpmRef.current ? Math.abs(rawBpm - lastRenderedBpmRef.current) : 100;
    if (valueDiff > 1 || timeSinceLastUpdate > 500) {
      setBpm(rawBpm);
      lastRenderedBpmRef.current = rawBpm;
      lastBpmUpdateTimeRef.current = now;
      // Update Stats
      setMinBpm(prev => prev === null ? rawBpm : Math.min(prev, rawBpm));
      setMaxBpm(prev => prev === null ? rawBpm : Math.max(prev, rawBpm));
      bpmHistoryRef.current.push(rawBpm);
      const sum = bpmHistoryRef.current.reduce((a, b) => a + b, 0);
      setAvgBpm(Math.round(sum / bpmHistoryRef.current.length));
      // Update Trend
      const history = bpmHistoryRef.current;
      if (history.length >= 5) {
        const recent = history.slice(-5);
        let increasing = 0;
        let decreasing = 0;
        for (let i = 1; i < recent.length; i++) {
          if (recent[i] > recent[i - 1]) increasing++;
          if (recent[i] < recent[i - 1]) decreasing++;
        }
        if (increasing >= 3) setTrend('up');else if (decreasing >= 3) setTrend('down');else setTrend('flat');
      }
    }
  }, [rawBpm, isConnected]);
  // --- Beating Heart Animation ---
  const [beatPulse, setBeatPulse] = useState(false);
  useEffect(() => {
    if (!isConnected || !bpm) return;
    const intervalMs = 60000 / bpm;
    const interval = setInterval(() => {
      setBeatPulse(true);
      setTimeout(() => setBeatPulse(false), 400); // Reset after animation
    }, intervalMs);
    return () => clearInterval(interval);
  }, [bpm, isConnected]);
  // --- Fixed-rate waveform playback ---
  useEffect(() => {
    const mainCanvas = mainCanvasRef.current;
    const miniCanvas = miniCanvasRef.current;
    if (!mainCanvas) return;
    const mainCtx = mainCanvas.getContext('2d');
    const miniCtx = miniCanvas?.getContext('2d') ?? null;
    if (!mainCtx) return;
    let previousFrameTime: number | null = null;
    let samplesDue = 0;
    const resizeCanvas = (canvas: HTMLCanvasElement) => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      if (!rect) return dpr;
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      return dpr;
    };
    const drawTrace = (context: CanvasRenderingContext2D, data: number[], width: number, height: number, color: string, glowColor: string) => {
      const visibleData = data.slice(-MAX_DISPLAYED_WAVEFORM_SAMPLES);
      if (visibleData.length < 2) return;
      const minValue = Math.min(...visibleData);
      const maxValue = Math.max(...visibleData);
      const observedRange = maxValue - minValue;
      const visualRange = Math.max(observedRange * 1.25, Number.EPSILON);
      const baseline = minValue - (visualRange - observedRange) / 2;
      const paddingY = height * 0.12;
      const usableHeight = height - paddingY * 2;
      const step = width / Math.max(MAX_DISPLAYED_WAVEFORM_SAMPLES - 1, 1);
      const startX = width - (visibleData.length - 1) * step;
      const mapY = (value: number) => height - paddingY - (value - baseline) / visualRange * usableHeight;
      const paint = (strokeStyle: string, lineWidth: number) => {
        context.beginPath();
        context.moveTo(startX, mapY(visibleData[0]));
        for (let index = 1; index < visibleData.length; index += 1) {
          context.lineTo(startX + index * step, mapY(visibleData[index]));
        }
        context.strokeStyle = strokeStyle;
        context.lineWidth = lineWidth;
        context.lineJoin = 'round';
        context.lineCap = 'round';
        context.stroke();
      };
      paint(glowColor, 6);
      paint(color, 1.5);
      context.beginPath();
      context.arc(width, mapY(visibleData[visibleData.length - 1]), 4, 0, Math.PI * 2);
      context.fillStyle = color;
      context.fill();
    };
    const draw = (frameTime: number) => {
      const dpr = resizeCanvas(mainCanvas);
      if (miniCanvas) resizeCanvas(miniCanvas);
      const elapsedSeconds = previousFrameTime === null ? 0 : (frameTime - previousFrameTime) / 1000;
      previousFrameTime = frameTime;
      samplesDue += elapsedSeconds * WAVEFORM_SAMPLES_PER_SECOND;
      let babyAdvanced = false;
      while (samplesDue >= 1) {
        samplesDue -= 1;
        const babySample = babyQueueRef.current.shift();
        if (babySample !== undefined) {
          mainBufferRef.current.push(babySample);
          if (mainBufferRef.current.length > MAX_DISPLAYED_WAVEFORM_SAMPLES) mainBufferRef.current.shift();
          babyAdvanced = true;
        }
        const motherSample = motherQueueRef.current.shift();
        if (motherSample !== undefined) {
          miniBufferRef.current.push(motherSample);
          if (miniBufferRef.current.length > MAX_DISPLAYED_WAVEFORM_SAMPLES) miniBufferRef.current.shift();
        }
      }
      if (babyQueueRef.current.length === 0) samplesDue = 0;
      const babySignalWeak = hasLiveBabyPlaybackRef.current && babyQueueRef.current.length === 0;
      const motherSignalWeak = motherQueueRef.current.length === 0;
      if (babySignalWeakRef.current !== babySignalWeak) {
        babySignalWeakRef.current = babySignalWeak;
        setIsBabySignalWeak(babySignalWeak);
      }
      if (motherSignalWeakRef.current !== motherSignalWeak) {
        motherSignalWeakRef.current = motherSignalWeak;
        setIsMotherSignalWeak(motherSignalWeak);
      }
      if (babyAdvanced) scrollOffsetRef.current += 1;
      const mainWidth = mainCanvas.width / dpr;
      const mainHeight = mainCanvas.height / dpr;
      mainCtx.save();
      mainCtx.scale(dpr, dpr);
      mainCtx.fillStyle = '#1A1211';
      mainCtx.fillRect(0, 0, mainWidth, mainHeight);
      mainCtx.strokeStyle = 'rgba(255,255,255,0.04)';
      mainCtx.lineWidth = 1;
      mainCtx.beginPath();
      for (let index = 1; index < 5; index += 1) {
        const y = mainHeight / 5 * index;
        mainCtx.moveTo(0, y);
        mainCtx.lineTo(mainWidth, y);
      }
      const scrollMod = scrollOffsetRef.current % 50;
      for (let x = -scrollMod; x < mainWidth; x += 50) {
        mainCtx.moveTo(x, 0);
        mainCtx.lineTo(x, mainHeight);
      }
      mainCtx.stroke();
      drawTrace(mainCtx, mainBufferRef.current, mainWidth, mainHeight, '#6B21E8', 'rgba(107, 33, 232, 0.16)');
      mainCtx.restore();
      if (miniCanvas && miniCtx) {
        const miniWidth = miniCanvas.width / dpr;
        const miniHeight = miniCanvas.height / dpr;
        miniCtx.save();
        miniCtx.scale(dpr, dpr);
        miniCtx.clearRect(0, 0, miniWidth, miniHeight);
        drawTrace(miniCtx, miniBufferRef.current, miniWidth, miniHeight, '#00D4D4', 'rgba(0, 212, 212, 0.16)');
        miniCtx.restore();
      }
      animationRef.current = requestAnimationFrame(draw);
    };
    animationRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);
  // --- Render Helpers ---
  const getSignalQualityProps = () => {
    switch (signalQuality) {
      case 'good':
        return {
          color: '#00D4D4',
          width: '100%',
          label: 'Good'
        };
      case 'fair':
        return {
          color: '#F59E0B',
          width: '60%',
          label: 'Fair'
        };
      case 'poor':
        return {
          color: '#E8735A',
          width: '30%',
          label: 'Poor'
        };
      default:
        return {
          color: '#8A8A8A',
          width: '0%',
          label: 'No Signal'
        };
    }
  };
  const sqProps = getSignalQualityProps();
  return <main className="fixed inset-0 bg-[#1A1211] overflow-hidden flex flex-col lg:flex-row z-[100]" data-id="element-1448" data-mp="1rv1x8dtglht2">
      {/* TOP BAR (Hidden in landscape mobile) */}
      {!isLandscape && <div className="absolute top-0 left-0 right-0 h-[80px] pt-[env(safe-area-inset-top)] px-4 flex items-center justify-between z-20 bg-gradient-to-b from-[#1A1211]/80 to-transparent" data-id="element-1449" data-mp="21lerixr2spce">
          <button onClick={() => navigate('/app/dashboard')} className="w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="Go back" data-id="element-1450" data-mp="25l5fyut4qtdr">
            <ChevronDown size={24} className="text-white/80" data-id="element-1451" data-mp="3q498zx8vrp1t" />
          </button>
          <span className="text-white text-[16px] font-semibold" data-id="element-1452" data-mp="3m2np3rxxiai4">
            Live Monitor
          </span>
          <div className="w-[100px] flex justify-end items-center space-x-3" data-id="element-1453" data-mp="3h5e36m9r0gz1">
            <button onClick={toggleSound} className="w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-1454" data-mp="hpqv3ew9dfaa">
              {soundEnabled ? <Volume2 size={20} className="text-accent" data-id="element-1455" data-mp="3wribknv1z6f" /> : <VolumeX size={20} className="text-white/80" data-id="element-1456" data-mp="11dq8kxi7j0je" />}
            </button>
            {activeSessionId && <div className="flex items-center space-x-2" data-id="element-1457" data-mp="wkx2o6b05m5q">
                <div className="w-2 h-2 rounded-full bg-danger animate-pulse" data-id="element-1458" data-mp="4yetst9pybps" />
                <span className="text-white/80 text-[13px] hidden sm:inline" data-id="element-1459" data-mp="9pn6by6cid76">
                  Recording
                </span>
              </div>}
          </div>
        </div>}

      {/* LEFT PANEL / MAIN CANVAS */}
      <div className={`relative w-full ${isLandscape ? 'h-full' : 'h-[100dvh] lg:h-full lg:w-[65%]'} flex-shrink-0`} onClick={() => isLandscape && setShowLandscapeStats(!showLandscapeStats)} data-id="element-1460" data-mp="vj23c8ch2l">
        <div className="absolute inset-0 w-full h-full" data-id="element-1461" data-mp="snpxaiigek7m">
          <canvas ref={mainCanvasRef} className="w-full h-full block" role="img" aria-label="Live fetal waveform played back at 50 samples per second" data-id="element-1462" data-mp="ephuvv2neqp2" />
        </div>
        {beltConnectionState === 'signal_weak' && <div className="absolute left-4 top-[92px] z-10 inline-flex items-center gap-2 rounded-pill border border-amber-400/30 bg-[#221A19]/90 px-3 py-1.5 text-[12px] font-semibold text-amber-300 shadow-lg lg:top-auto lg:bottom-4" role="status" aria-live="polite" data-id="element-1463" data-mp="33garj04ymilm">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-300" data-id="element-1464" data-mp="35vh6x380dh2w" />
            Signal weak · trace paused
          </div>}

        {/* Gradient Overlay for mobile/tablet portrait */}
        {!isLandscape && <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-gradient-to-b from-transparent to-[#1A1211] lg:hidden" data-id="element-1465" data-mp="2d7bmyios3w44" />}
      </div>

      {/* RIGHT PANEL / STATS & CONTROLS */}
      {(!isLandscape || showLandscapeStats) && <div id={!isLandscape ? 'monitor-mobile-panel' : undefined} className={`${isLandscape ? 'absolute bottom-4 right-4 bg-[#221A19]/90 backdrop-blur-md rounded-[24px] p-4 flex flex-row items-center space-x-6' : `fixed inset-x-0 bottom-0 z-30 flex h-[82dvh] flex-col overflow-y-auto rounded-t-[28px] border-t border-white/10 bg-[#221A19]/95 px-6 pb-[calc(24px+env(safe-area-inset-bottom))] pt-3 shadow-2xl backdrop-blur-md transition-transform duration-300 ease-out ${isMobilePanelOpen ? 'translate-y-0' : 'translate-y-[calc(100%-118px)]'} lg:static lg:h-auto lg:flex-1 lg:translate-y-0 lg:rounded-none lg:border-0 lg:bg-[#221A19] lg:p-8 lg:shadow-none lg:backdrop-blur-none`}`} data-id="element-1466" data-mp="2i5mvmq3c80kr">
          {!isLandscape && <button type="button" onClick={() => setIsMobilePanelOpen(open => !open)} className="mx-auto mb-3 flex w-full flex-col items-center gap-2 lg:hidden" aria-label={isMobilePanelOpen ? 'Collapse monitor readings' : 'Expand monitor readings'} data-id="element-1467" data-mp="3lfgklg1s7nc4">
              <span className="h-1 w-10 rounded-pill bg-white/30" data-id="element-1468" data-mp="250os734myg2t" />
              <span className="text-[11px] font-semibold uppercase tracking-[1.5px] text-white/45" data-id="element-1469" data-mp="2531hy2gmxuqf">
                {isMobilePanelOpen ? 'Live readings' : 'Tap for readings'}
              </span>
            </button>}
          {/* BPM Hero Display */}
          <div className={`flex flex-col ${isLandscape ? 'items-start' : 'items-center lg:items-start lg:mt-12'}`} data-id="element-1470" data-mp="1uw00ml6rrzpe">
            <div className="flex items-start" data-id="element-1471" data-mp="1h425je9w84c8">
              <motion.div key={bpm} initial={{
            y: -4,
            scale: 1.05
          }} animate={{
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.25
          }} className={`font-bold text-primary leading-none ${isLandscape ? 'text-[48px]' : 'text-[72px] lg:text-[96px]'}`} aria-live="polite" aria-label={`Current BPM: ${isConnected && bpm ? bpm : 'Unknown'}`} data-id="element-1472" data-mp="1m3y2wuo34xit">
                {isConnected && bpm ? bpm : '--'}
              </motion.div>
              <div className="ml-2 mt-2 flex flex-col items-center" data-id="element-1473" data-mp="2v5zn8zkqmqzd">
                {trend === 'up' && <TrendingUp size={24} className="text-accent mb-1" data-id="element-1474" data-mp="2r3mt78fcshif" />}
                {trend === 'down' && <TrendingDown size={24} className="text-danger mb-1" data-id="element-1475" data-mp="2m5bjcxp26707" />}
                {trend === 'flat' && <Minus size={24} className="text-white/40 mb-1" data-id="element-1476" data-mp="2h81np0b2sz3x" />}
              </div>
            </div>
            <span className="text-white/50 text-[16px] mt-1" data-id="element-1477" data-mp="39umf20rjxb7h">Fetal BPM</span>

            <div className="mt-4" data-id="element-1478" data-mp="39wqfg8q8rs30">
              <div className={`inline-flex items-center gap-2 rounded-pill px-3 py-1.5 text-[13px] font-semibold ${beltStatus.className}`} role="status" aria-live="polite" data-id="element-1479" data-mp="352vyhd20tr7m">
                <span className="relative flex h-2 w-2" data-id="element-1480" data-mp="1fvljirf9i12">
                  {beltStatus.showPing && <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" data-id="element-1481" data-mp="3srxcdftmhuka" />}
                  <span className={`relative inline-flex h-2 w-2 rounded-full ${beltStatus.dotClassName}`} data-id="element-1482" data-mp="8xtv1n9lst4a" />
                </span>
                {beltStatus.label}
              </div>
              {beltConnectionState !== 'live' && beltConnectionState !== 'signal_weak' && <p className="mt-2 max-w-[260px] text-center text-[12px] leading-relaxed text-white/55 lg:text-left" data-id="element-1483" data-mp="mymoe4o65i5t">
                  {beltStatus.message}
                </p>}
            </div>

            <div className="mt-4 rounded-[16px] border border-white/10 bg-white/5 px-4 py-3 min-w-[156px]" data-id="element-1484" data-mp="wopgm2ngus33">
              <span className="block text-[11px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1485" data-mp="11lfx2r36npp1">
                Maternal BPM
              </span>
              <span className="text-[28px] font-semibold text-accent leading-none tabular-nums" aria-live="polite" aria-label={`Maternal BPM: ${isConnected && maternalBpm !== null ? maternalBpm : 'Unknown'}`} data-id="element-1486" data-mp="1anfcxg2gonqe">
                {isConnected && maternalBpm !== null ? maternalBpm : '--'}
              </span>
            </div>

            {/* Beating Heart */}
            {!isLandscape && <div className="relative mt-6 mb-8 flex items-center justify-center lg:justify-start w-full" data-id="element-1487" data-mp="2d4btcmb0a74n">
                <motion.div animate={beatPulse ? {
            scale: [1, 1.3, 1]
          } : {
            scale: 1
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className={`relative z-10 ${!isConnected ? 'opacity-40' : ''}`} data-id="element-1488" data-mp="1pdzip3odlwad">
                  <Heart size={40} className="text-primary fill-primary" data-id="element-1489" data-mp="34wawptqh8c7b" />
                </motion.div>

                {/* Ripple */}
                {beatPulse && isConnected && <motion.div initial={{
            scale: 1,
            opacity: 0.5
          }} animate={{
            scale: 2.5,
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="absolute w-[40px] h-[40px] rounded-full border-2 border-primary lg:ml-0" data-id="element-1490" data-mp="3he55xlaboska" />}

                {!isConnected && <div className="absolute top-12 flex items-center space-x-2" data-id="element-1491" data-mp="3raghua603ge7">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" data-id="element-1492" data-mp="iv6mdkwm99fe" />
                    <span className="text-white/50 text-[13px]" data-id="element-1493" data-mp="nmev0eq4gqri">
                      Reconnecting...
                    </span>
                  </div>}
              </div>}
          </div>

          {/* Stats Row */}
          <div className={`flex ${isLandscape ? 'space-x-6 ml-6 border-l border-white/10 pl-6' : 'justify-between w-full lg:flex-col lg:space-y-6 lg:mt-8'}`} data-id="element-1494" data-mp="sjlmezy41zud">
            <div className="flex flex-col" data-id="element-1495" data-mp="jhce8gqbeik5">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1496" data-mp="ej12u1kdfzzt">
                Min
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1497" data-mp="9s16axf5zjxu">
                {minBpm || '--'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-1498" data-mp="4thkcx0km85h">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1499" data-mp="3vzsmkwhbheye">
                Max
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1500" data-mp="3rwmah87iwtk0">
                {maxBpm || '--'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-1501" data-mp="3n16zqp4tbdrt">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1502" data-mp="22hlgb6p2yy3r">
                Avg
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1503" data-mp="27ebutpl8lln2">
                {avgBpm || '--'}
              </span>
            </div>
          </div>

          {/* Signal Quality & Uterine Activity (Hidden in landscape) */}
          {!isLandscape && <div className="w-full mt-8 lg:mt-12 space-y-8" data-id="element-1504" data-mp="1jr3nz385u4ix">
              {/* Signal Quality */}
              <div data-id="element-1505" data-mp="1nr4dpk0pm25a">
                <span className="text-[12px] text-white/50 uppercase tracking-[1px] block mb-2" data-id="element-1506" data-mp="1nsp9fcyrfr4q">
                  Signal Quality
                </span>
                <div className="w-full h-[6px] bg-white/10 rounded-pill overflow-hidden mb-2" data-id="element-1507" data-mp="1xhpbj58k72n5">
                  <div className="h-full rounded-pill transition-all duration-500" style={{
              width: sqProps.width,
              backgroundColor: sqProps.color
            }} data-id="element-1508" data-mp="102bbx6xep02r" />
                </div>
                <span className="text-[13px]" style={{
            color: sqProps.color
          }} data-id="element-1509" data-mp="3pkd8y5v8ce4g">
                  {sqProps.label}
                </span>
              </div>

              {/* Maternal waveform */}
              <div data-id="element-1510" data-mp="3fje8krh3s13">
                <div className="mb-2 flex items-center justify-between gap-3" data-id="element-1511" data-mp="hcofm7g8s2q0">
                  <span className="text-[12px] text-white/50 uppercase tracking-[1px]" data-id="element-1512" data-mp="halfuvpaev83">
                    Maternal waveform
                  </span>
                  {isMotherSignalWeak && <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-300" data-id="element-1513" data-mp="r4kf9mxz80wc">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-300" data-id="element-1514" data-mp="103flv04xnd06" />
                      {isConnected ? 'Signal weak' : 'Reconnecting'}
                    </span>}
                </div>
                <div className="w-full h-[40px] relative mb-1" data-id="element-1515" data-mp="1eqedav3si7ew">
                  <canvas ref={miniCanvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label="Live maternal waveform played back at 50 samples per second" data-id="element-1516" data-mp="1eo1u6emapr09" />
                </div>
                <span className="text-[14px] text-white/60" data-id="element-1517" data-mp="19t4p1is501sn">
                  {isConnected && maternalBpm !== null ? `${maternalBpm.toFixed(1)} BPM` : '--'}
                </span>
              </div>

              {/* Baby Activity */}
              <div className="bg-white/5 rounded-[16px] p-4 relative overflow-hidden" data-id="element-1518" data-mp="m1qt72us1o2x">
                <div className="flex justify-between items-center mb-4" data-id="element-1519" data-mp="r0k0uendukv6">
                  <span className="text-[12px] text-white/50 uppercase tracking-[1px]" data-id="element-1520" data-mp="qyhbzxxyuxs1">
                    Baby Activity 👶
                  </span>
                  <span className="text-[12px] text-white/50" data-id="element-1521" data-mp="i05b933dyjlv">
                    Kicks this session:{' '}
                    <span className="text-white font-medium" data-id="element-1522" data-mp="314a61os05pul">{kickCount}</span>
                  </span>
                </div>

                <div className="flex justify-center items-center h-[100px] relative" data-id="element-1523" data-mp="355c2xyyg5pte">
                  <AnimatePresence data-id="element-1524" data-mp="3a35d132vf3yy">
                    {showKickText && <motion.div initial={{
                opacity: 0,
                y: 10,
                x: 20
              }} animate={{
                opacity: 1,
                y: -20,
                x: 20
              }} exit={{
                opacity: 0
              }} className="absolute text-accent font-bold text-[14px] z-10" data-id="element-1525" data-mp="3f39r6mgb3mm0">
                        +kick
                      </motion.div>}
                  </AnimatePresence>

                  <motion.div animate={!isConnected ? {
              scale: 1,
              opacity: 0.4
            } : showKick ? {
              scale: 1.05,
              x: 4,
              rotate: 3
            } : {
              scale: [1, 1.03, 1]
            }} transition={showKick ? {
              duration: 0.15,
              yoyo: 1
            } : {
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative" data-id="element-1526" data-mp="1w4lobzesve6l">
                    <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1527" data-mp="qz580pmipb20">
                      {/* Stylized baby silhouette */}
                      <path d="M50 20C40 20 32 28 32 38C32 42 33.5 45.5 36 48.5C30 52 25 58 25 65C25 76 36 85 50 85C64 85 75 76 75 65C75 58 70 52 64 48.5C66.5 45.5 68 42 68 38C68 28 60 20 50 20Z" fill="#6B21E8" opacity="0.8" data-id="element-1528" data-mp="m3zd51b50m1s" />
                      <circle cx="45" cy="35" r="3" fill="white" opacity="0.6" data-id="element-1529" data-mp="377wz0qw6n1y" />
                      <path d="M40 42Q45 45 50 42" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.6" data-id="element-1530" data-mp="1xlqyazzp6kie" />
                      <path d="M35 65Q50 75 65 65" stroke="#00D4D4" strokeWidth="3" strokeLinecap="round" opacity="0.8" data-id="element-1531" data-mp="3rtcpebwcf96e" />
                    </svg>
                  </motion.div>
                </div>
              </div>
            </div>}

          {/* Bottom Controls */}
          {!isLandscape && <div className="mt-auto pt-6 lg:pt-12 w-full flex space-x-3 pb-[env(safe-area-inset-bottom)]" data-id="element-1532" data-mp="eli2usvft37r">
              <button onClick={() => setShowEndSession(true)} className="flex-1 h-[52px] bg-danger/20 border border-danger text-danger rounded-[14px] flex items-center justify-center space-x-2 font-medium" data-id="element-1533" data-mp="9o9f8cris6z4">
                <Square size={18} className="fill-danger" data-id="element-1534" data-mp="12io2dk0z3d5i" />
                <span data-id="element-1535" data-mp="12k8xpbv4304f">End Session</span>
              </button>
              <button className="w-[52px] h-[52px] bg-white/10 border border-white/20 text-white/80 rounded-[14px] flex items-center justify-center" aria-label="Share" data-id="element-1536" data-mp="17g6flmm0wejb">
                <Share2 size={20} data-id="element-1537" data-mp="1ghe091vm9gux" />
              </button>
              <button onClick={() => navigate('/app/history')} className="w-[52px] h-[52px] bg-white/10 border border-white/20 text-white/80 rounded-[14px] flex items-center justify-center" aria-label="View history" data-id="element-1538" data-mp="js6p8nsmiq59">
                <BarChart2 size={20} data-id="element-1539" data-mp="xj1nz79erqv4" />
              </button>
            </div>}
        </div>}

      {/* END SESSION BOTTOM SHEET */}
      <AnimatePresence data-id="element-1540" data-mp="10duantzuuuw">
        {showEndSession && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowEndSession(false)} className="fixed inset-0 bg-black/60 z-[110]" data-id="element-1541" data-mp="528t9logcw5i" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-[#221A19] rounded-t-[24px] p-6 pb-[calc(24px+env(safe-area-inset-bottom))] z-[120] lg:max-w-[400px] lg:left-1/2 lg:-translate-x-1/2 lg:bottom-6 lg:rounded-[24px]" data-id="element-1542" data-mp="3kq1yl4s8x2m9">
              <div className="w-[40px] h-[4px] bg-white/20 rounded-pill mx-auto mb-6 lg:hidden" data-id="element-1543" data-mp="2s1ct6tk5qb4p" />
              <h2 className="text-[20px] font-semibold text-white mb-2" data-id="element-1544" data-mp="2n2a087hgbvd3">
                End this session?
              </h2>
              <p className="text-[15px] text-white/60 mb-8" data-id="element-1545" data-mp="2ibec3lp6n5mw">
                Your session data will be saved and synced. You can review it in
                History.
              </p>
              <div className="flex flex-col space-y-3" data-id="element-1546" data-mp="23groshnk9z8n">
                <button onClick={handleEndSession} disabled={isEndingSession} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium flex items-center justify-center" data-id="element-1547" data-mp="28e9orijlp44i">
                  {isEndingSession ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1548" data-mp="2mchsc9b573on" /> : 'Yes, End Session'}
                </button>
                <button onClick={() => setShowEndSession(false)} disabled={isEndingSession} className="w-full h-[52px] bg-transparent text-white/70 rounded-[14px] font-medium" data-id="element-1549" data-mp="3f0lvoo68r4o6">
                  Keep Monitoring
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}